import React, { useState, useEffect, useMemo } from 'react';
import {
    X, RefreshCw, Loader2, Archive, User, Camera, FileText,
    FolderOpen, Folder, ChevronRight, Tag, Image as ImageIcon,
    PlayCircle, Download, Calendar, Trash2, CheckSquare, Square, Lock
} from 'lucide-react';
import { Incarico, IncaricoMedia } from '../types';
import { IncaricoService } from '../services/apiService';
import { isIncaricoSingleDay, extractDateOnly } from '../utils/incaricoUtils';
import { useToast } from '../components/ToastContext';
import { InfoTab } from '../components/mieiIncarichi/InfoTab';
import { OpsTab } from '../components/mieiIncarichi/OpsTab';
import { ReportTab } from '../components/mieiIncarichi/ReportTab';
import { PreviewModal } from '../components/mieiIncarichi/PreviewModal';
import { NoteModal } from '../components/mieiIncarichi/NoteModal';
import { MissingDateModal } from '../components/mieiIncarichi/MissingDateModal';
import { PendingWarningModal } from '../components/mieiIncarichi/PendingWarningModal';
import { isBrowserPlayableVideoUrl } from '../utils/videoPlayback';

interface Props {
    activeIncarico: Incarico;
    isStoricoActive: boolean;
    isFuturoActive: boolean;
    pendingEvidence: { hasPending: boolean; sessions: number };
    actionLoading: boolean;
    uploadingFiles: { [key: string]: number };
    uploadQueue: { isUploading: boolean; total: number; completed: number };
    fotoDevice: string;
    setFotoDevice: (v: string) => void;
    videoDevice: string;
    setVideoDevice: (v: string) => void;
    missingDateQueue: { file: File; category: string; preview: string }[];
    setMissingDateQueue: (q: any) => void;
    currentMissingDateIndex: number;
    setCurrentMissingDateIndex: (i: any) => void;
    manualFileDate: string;
    setManualFileDate: (v: string) => void;
    pollingInterval: React.MutableRefObject<NodeJS.Timeout | null>;
    onBack: () => void;
    onRefresh: (background?: boolean) => void;
    getPos: () => Promise<GeolocationPosition>;
    getFreshUrl: (item: any) => Promise<string>;
    onStartWithPhoto: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onStopWithPhoto: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onFilesMultiUpload: (e: React.ChangeEvent<HTMLInputElement>, category: string) => void;
    onManualDateSubmit: () => void;
    onSkipMissingDate: () => void;
    onAccetta: () => void;
    onRifiuta: (motivo?: string) => void;
    // Apre un incarico urgente ereditando la pratica di quello attuale.
    onCreateUrgente: () => void;
    // Deep-link dalla Dashboard: tab e data iniziali per aprire direttamente
    // il report da modificare del giorno giusto.
    initialActiveTab?: 'info' | 'ops' | 'spese' | 'archivio';
    initialSelectedDate?: string;
}

// Tab archivio. Riconoscimento video come in TabMedia: elenco completo di
// estensioni più fallback sul MIME type, così anche i .mts (convertiti in mp4
// dal BE) finiscono nel ramo video.
const VIDEO_EXTS = ['mp4', 'mov', 'avi', 'mts', 'm2ts', 'mkv', 'webm', 'm4v', 'mpg', 'mpeg'];
const isVideoFile = (item: any) => {
    const ext = (item?.filename as string)?.split('.').pop()?.toLowerCase() || '';
    return VIDEO_EXTS.includes(ext) || !!item?.fileType?.includes('video');
};

interface ArchivioTabProps {
    media: any[];
    getFreshUrl: (item: any) => Promise<string>;
    // Richiesta di eliminazione da parte del collaboratore: DELETE /media/{id}
    // per lui crea una DELETION_REQUEST da far approvare al titolare.
    onRequestDelete: (mediaId: number, filename: string) => Promise<void>;
    // Richiesta di eliminazione multipla: il chiamante fa una DELETE per id e mostra
    // un toast unico; il backend decide per ruolo su ciascuno.
    onRequestBatchDelete: (mediaIds: number[]) => Promise<void>;
}

const ArchivioTab: React.FC<ArchivioTabProps> = ({ media, getFreshUrl, onRequestDelete, onRequestBatchDelete }) => {
    const [requestingDelete, setRequestingDelete] = useState(false);
    const [path, setPath] = useState<string[]>([]); // [anno, mese, giorno, categoria]
    const [previewItem, setPreviewItem] = useState<any | null>(null);
    const [freshUrl, setFreshUrl] = useState<string | null>(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    // Selezione multipla per la richiesta di eliminazione: in selezione il tap
    // sulla card spunta invece di aprire l'anteprima. La selezione si azzera
    // all'uscita dalla modalità e a ogni cambio di cartella.
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [batchRequesting, setBatchRequesting] = useState(false);

    const toggleSelection = (id: number) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const exitSelection = () => {
        setSelectionMode(false);
        setSelectedIds(new Set());
    };

    // Escludiamo solo le foto km (Km_Iniziali_… / Km_Finali_…) e i media senza
    // dataAcquisizione o non visualizzabili. I file "WhatsApp ..." si vedono come
    // gli altri, come nell'ArchivioTab dell'app.
    const allMedia = useMemo(() =>
            media.filter(m =>
                !m.filename?.startsWith('Km_') &&
                m.dataAcquisizione &&
                m.analysisStatus !== 'DUPLICATE' &&
                m.analysisStatus !== 'OUTSIDE_RANGE' &&
                m.analysisStatus !== 'PENDING_APPROVAL'
            ).map(m => {
                // Prima il campo `categoria` dell'entity, poi il prefisso `[CATEGORIA]` nel
                // filename dei vecchi upload.
                const catMatch = (m.filename as string)?.match(/^\[(.*?)\]/);
                const category = m.categoria
                    ? String(m.categoria).toUpperCase()
                    : (catMatch ? catMatch[1].toUpperCase() : 'NON CATEGORIZZATO');
                const d = new Date(m.dataAcquisizione || m.uploadTime || m.createdAt);
                return {
                    ...m,
                    category,
                    cleanName: m.filename?.replace(/^\[.*?\]\s*/, '') || m.filename,
                    dateObj: d,
                    anno: d.getFullYear().toString(),
                    mese: d.toLocaleString('it-IT', { month: 'long' }).toUpperCase(),
                    giorno: d.getDate().toString().padStart(2, '0'),
                    ora: d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }),
                };
            }),
        [media]
    );

    // Naviga la struttura ad albero
    const viewData = useMemo(() => {
        let filtered = allMedia;
        if (path[0]) filtered = filtered.filter(m => m.anno === path[0]);
        if (path[1]) filtered = filtered.filter(m => m.mese === path[1]);
        if (path[2]) filtered = filtered.filter(m => m.giorno === path[2]);
        if (path[3]) filtered = filtered.filter(m => m.category === path[3]);

        if (path.length === 0) {
            const items = Array.from(new Set(filtered.map(m => m.anno))).sort((a,b) => b.localeCompare(a));
            return { type: 'folders' as const, items, icon: <Calendar size={28} className="text-indigo-500" /> };
        }
        if (path.length === 1) {
            const items = Array.from(new Set(filtered.map(m => m.mese)));
            return { type: 'folders' as const, items, icon: <Calendar size={28} className="text-blue-500" /> };
        }
        if (path.length === 2) {
            const items = Array.from(new Set(filtered.map(m => m.giorno))).sort();
            return { type: 'folders' as const, items, icon: <Calendar size={28} className="text-emerald-500" /> };
        }
        if (path.length === 3) {
            const items = Array.from(new Set(filtered.map(m => m.category)));
            return { type: 'folders' as const, items, icon: <Tag size={28} className="text-amber-500" /> };
        }
        return { type: 'files' as const, items: filtered.sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime()), icon: null };
    }, [allMedia, path]);

    const openPreview = async (item: any) => {
        setPreviewItem(item);
        setLoadingPreview(true);
        setFreshUrl(null);
        try {
            const url = await getFreshUrl({ ...item, isFieldMedia: true });
            setFreshUrl(url);
        } catch {
            setFreshUrl(item.urlCompressed || item.finalUrl);
        } finally {
            setLoadingPreview(false);
        }
    };

    const breadcrumbLabels = ['Anno', 'Mese', 'Giorno', 'Categoria'];

    // Cambiando cartella si esce dalla selezione: gli id scelti altrove non
    // avrebbero più senso.
    useEffect(() => {
        if (selectionMode) exitSelection();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [path.join('/')]);

    const handleBatchDelete = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        const ok = window.confirm(
            `Richiedere al titolare l'eliminazione di ${ids.length} file?\n\n`
            + `I file resteranno visibili fino all'approvazione.`
        );
        if (!ok) return;
        setBatchRequesting(true);
        try {
            await onRequestBatchDelete(ids);
            exitSelection();
        } finally {
            setBatchRequesting(false);
        }
    };

    return (
        <div className="space-y-4 pb-24">
            {/* Breadcrumb + toggle selezione multipla */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <button
                        onClick={() => setPath([])}
                        className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase transition-colors ${path.length === 0 ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                    >
                        Archivio
                    </button>
                    {path.map((seg, i) => (
                        <React.Fragment key={i}>
                            <ChevronRight size={12} className="text-slate-300" />
                            <button
                                onClick={() => setPath(path.slice(0, i + 1))}
                                className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase transition-colors ${i === path.length - 1 ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                            >
                                {seg}
                            </button>
                        </React.Fragment>
                    ))}
                </div>
                {/*
                  Toggle selezione, visibile solo se nella cartella ci sono file. Disponibile
                  anche su incarichi REVOCATO: il collaboratore può dover chiedere la rimozione
                  di media sensibili; resta comunque una richiesta da approvare.
                */}
                {viewData.type === 'files' && viewData.items.length > 0 && (
                    selectionMode ? (
                        <button
                            onClick={exitSelection}
                            className="px-3 py-1.5 rounded-lg text-[10px] font-black uppercase bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors flex items-center gap-1.5"
                        >
                            <X size={12} /> Annulla
                        </button>
                    ) : (
                        <button
                            onClick={() => setSelectionMode(true)}
                            className="px-3 py-1.5 rounded-lg text-[10px] font-black uppercase bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors flex items-center gap-1.5"
                        >
                            <CheckSquare size={12} /> Seleziona
                        </button>
                    )
                )}
            </div>

            {/* Contatore */}
            <p className="text-[9px] font-bold text-slate-400 uppercase px-0.5">
                {viewData.type === 'files' ? `${viewData.items.length} file` : `${viewData.items.length} ${breadcrumbLabels[path.length] || 'elementi'}`}
            </p>

            {/* Cartelle */}
            {viewData.type === 'folders' && (
                <div className="grid grid-cols-3 gap-3">
                    {viewData.items.length === 0 && (
                        <div className="col-span-3 py-12 text-center text-slate-400">
                            <FolderOpen size={36} className="mx-auto mb-3 opacity-30" />
                            <p className="text-xs font-bold uppercase">Nessun file</p>
                        </div>
                    )}
                    {viewData.items.map(item => (
                        <button
                            key={item}
                            onClick={() => setPath([...path, item])}
                            className="group flex flex-col items-center gap-2.5 bg-white border border-slate-200 rounded-2xl p-4 hover:border-blue-400 hover:shadow-md transition-all active:scale-95"
                        >
                            <div className="p-3 bg-slate-50 rounded-full group-hover:bg-blue-50 transition-colors">
                                {viewData.icon}
                            </div>
                            <span className="text-[10px] font-black text-slate-700 uppercase text-center leading-tight">{item}</span>
                        </button>
                    ))}
                </div>
            )}

            {/* File */}
            {viewData.type === 'files' && (
                <div className="grid grid-cols-3 gap-2.5">
                    {viewData.items.length === 0 && (
                        <div className="col-span-3 py-12 text-center text-slate-400">
                            <ImageIcon size={36} className="mx-auto mb-3 opacity-30" />
                            <p className="text-xs font-bold uppercase">Nessun file</p>
                        </div>
                    )}
                    {(viewData.items as any[]).map((m) => {
                        const isVideo = isVideoFile(m);
                        const thumb = m.urlCompressed || m.finalUrl;
                        const isSelected = selectionMode && selectedIds.has(m.id);
                        return (
                            <div key={m.id} className="flex flex-col gap-1">
                                <div
                                    onClick={() => selectionMode ? toggleSelection(m.id) : openPreview(m)}
                                    className={`group relative aspect-square rounded-xl overflow-hidden bg-slate-900 border cursor-pointer hover:shadow-md transition-all active:scale-95 ${
                                        isSelected
                                            ? 'border-blue-500 ring-2 ring-blue-300'
                                            : 'border-slate-200 hover:border-blue-400'
                                    }`}
                                >
                                    {thumb ? (
                                        // Per i video la thumbnail compressa è un .mp4 che <img> non mostra: usiamo
                                        // <video> con seek a 0.1s per il primo frame. pointer-events-none lascia
                                        // il click al wrapper.
                                        isVideo ? (
                                            <video
                                                src={thumb}
                                                preload="metadata"
                                                muted
                                                playsInline
                                                onLoadedMetadata={(e) => { try { e.currentTarget.currentTime = 0.1; } catch {} }}
                                                className={`w-full h-full object-cover pointer-events-none transition-opacity ${isSelected ? 'opacity-60' : 'opacity-90 group-hover:opacity-100'}`}
                                            />
                                        ) : (
                                            <img src={thumb} alt={m.cleanName} className={`w-full h-full object-cover pointer-events-none transition-opacity ${isSelected ? 'opacity-60' : 'opacity-90 group-hover:opacity-100'}`} />
                                        )
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <ImageIcon size={24} className="text-slate-600" />
                                        </div>
                                    )}
                                    {isVideo && !selectionMode && (
                                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                            <PlayCircle className="text-white drop-shadow-lg" size={28} />
                                        </div>
                                    )}
                                    {/* Checkbox in alto a sinistra in modalità selezione */}
                                    {selectionMode && (
                                        <div className={`absolute top-1.5 left-1.5 w-6 h-6 rounded-md flex items-center justify-center shadow-md ${
                                            isSelected ? 'bg-blue-600 text-white' : 'bg-white/95 text-slate-400 border border-slate-300'
                                        }`}>
                                            {isSelected ? <CheckSquare size={14}/> : <Square size={14}/>}
                                        </div>
                                    )}
                                </div>
                                <p className="text-[8px] font-black text-slate-500 text-center leading-tight truncate px-0.5">
                                    {m.ora}
                                </p>
                            </div>
                        );
                    })}
                </div>
            )}

            {/*
              Barra in basso con conteggio e richiesta di eliminazione, attiva in modalità
              selezione con almeno un elemento. Colore rosa per segnalare l'azione distruttiva.
            */}
            {selectionMode && selectedIds.size > 0 && (
                <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[200] bg-slate-900 text-white rounded-2xl shadow-2xl border border-slate-700 px-4 py-3 flex items-center gap-3 max-w-[90vw]">
                    <div className="flex flex-col leading-tight">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Selezionati</span>
                        <span className="text-base font-black">{selectedIds.size} file</span>
                    </div>
                    <button
                        onClick={exitSelection}
                        disabled={batchRequesting}
                        className="px-3 py-2 rounded-lg text-[11px] font-bold bg-slate-700 hover:bg-slate-600 disabled:opacity-50 transition-colors"
                    >Annulla</button>
                    <button
                        onClick={handleBatchDelete}
                        disabled={batchRequesting}
                        className="px-4 py-2 rounded-lg text-[11px] font-black uppercase bg-rose-600 hover:bg-rose-700 disabled:opacity-60 transition-colors flex items-center gap-1.5"
                    >
                        {batchRequesting
                            ? <><Loader2 size={13} className="animate-spin"/> Invio…</>
                            : <><Trash2 size={13}/> Richiedi eliminazione</>}
                    </button>
                </div>
            )}

            {/* Lightbox: il click sull'overlay chiude, il contenuto usa stopPropagation. */}
            {previewItem && (
                <div
                    className="fixed inset-0 z-[300] bg-slate-900/95 flex flex-col items-center justify-center p-4 animate-in fade-in"
                    onClick={() => { setPreviewItem(null); setFreshUrl(null); }}
                >
                    <button
                        onClick={(e) => { e.stopPropagation(); setPreviewItem(null); setFreshUrl(null); }}
                        className="absolute top-5 right-5 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
                    >
                        <X size={24} />
                    </button>

                    <div
                        className="w-full max-w-md flex flex-col items-center gap-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {loadingPreview ? (
                            <Loader2 className="animate-spin text-blue-400" size={48} />
                        ) : (freshUrl || previewItem.finalUrl || previewItem.urlCompressed) ? (
                            isVideoFile(previewItem) ? (
                                isBrowserPlayableVideoUrl(freshUrl || previewItem.urlCompressed || previewItem.finalUrl) ? (
                                    <video
                                        src={freshUrl || previewItem.urlCompressed || previewItem.finalUrl}
                                        controls autoPlay
                                        className="w-full max-h-[60vh] rounded-2xl object-contain bg-black shadow-2xl"
                                    />
                                ) : (
                                    // Originale non riproducibile (es. .MTS) e MP4 convertito non ancora pronto.
                                    <div className="w-full flex flex-col items-center text-center gap-3 py-12 px-6 rounded-2xl bg-slate-800 text-slate-200">
                                        <Loader2 className="animate-spin text-amber-400" size={36} />
                                        <p className="text-sm font-black">Video in elaborazione</p>
                                        <p className="text-xs text-slate-400 leading-relaxed">
                                            Il formato originale non è riproducibile nel browser: la versione convertita (MP4) sarà disponibile a breve.
                                        </p>
                                    </div>
                                )
                            ) : (
                                <img
                                    src={freshUrl || previewItem.urlCompressed || previewItem.finalUrl}
                                    alt={previewItem.cleanName}
                                    className="w-full max-h-[60vh] rounded-2xl object-contain shadow-2xl"
                                />
                            )
                        ) : (
                            <div className="text-slate-400 text-center">
                                <ImageIcon size={48} className="mx-auto mb-3 opacity-30" />
                                <p className="text-sm font-bold">Anteprima non disponibile</p>
                            </div>
                        )}

                        {/* Info file */}
                        <div className="w-full bg-slate-800/80 backdrop-blur-sm rounded-2xl p-4 border border-slate-700">
                            <p className="text-sm font-bold text-white truncate">{previewItem.cleanName}</p>
                            <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-2">
                                <Calendar size={10} />
                                {previewItem.dateObj.toLocaleDateString('it-IT')} ore {previewItem.ora}
                                <span className="ml-auto font-black text-slate-300 uppercase">{previewItem.category}</span>
                            </p>
                        </div>

                        {(freshUrl || previewItem.finalUrl) && (
                            <a
                                href={freshUrl || previewItem.finalUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="w-full py-3 bg-blue-600 text-white rounded-xl font-black text-xs uppercase text-center flex items-center justify-center gap-2 active:scale-95 transition-transform"
                            >
                                <Download size={14} /> Scarica originale
                            </a>
                        )}

                        {/*
                          Richiesta di eliminazione del collaboratore: il backend crea una
                          DELETION_REQUEST, il titolare la approva da Approvazioni e solo allora il
                          file viene rimosso. Disponibile anche su incarichi REVOCATO.
                        */}
                        <button
                            disabled={requestingDelete}
                            onClick={async () => {
                                const ok = window.confirm(
                                    `Richiedere al titolare l'eliminazione di "${previewItem.cleanName}"?\n\n`
                                    + `Il file resterà visibile fino all'approvazione.`
                                );
                                if (!ok) return;
                                setRequestingDelete(true);
                                try {
                                    await onRequestDelete(previewItem.id, previewItem.cleanName);
                                    setPreviewItem(null);
                                    setFreshUrl(null);
                                } finally {
                                    setRequestingDelete(false);
                                }
                            }}
                            className="w-full py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-black text-xs uppercase text-center flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {requestingDelete
                                ? <><Loader2 size={14} className="animate-spin" /> Invio richiesta…</>
                                : <><Trash2 size={14} /> Richiedi eliminazione</>}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// Componente principale
export const IncaricoDetailView: React.FC<Props> = ({
                                                        activeIncarico, isStoricoActive, isFuturoActive, pendingEvidence, actionLoading,
                                                        uploadingFiles, uploadQueue, fotoDevice, setFotoDevice, videoDevice, setVideoDevice,
                                                        missingDateQueue, setMissingDateQueue, currentMissingDateIndex, setCurrentMissingDateIndex,
                                                        manualFileDate, setManualFileDate, pollingInterval,
                                                        onBack, onRefresh, getPos, getFreshUrl,
                                                        onStartWithPhoto, onStopWithPhoto, onFilesMultiUpload,
                                                        onManualDateSubmit, onSkipMissingDate, onAccetta, onRifiuta,
                                                        onCreateUrgente,
                                                        initialActiveTab, initialSelectedDate,
                                                    }) => {
    const { addToast } = useToast();
    const [activeTab, setActiveTab] = useState<'info' | 'ops' | 'spese' | 'archivio'>(initialActiveTab ?? 'info');
    // selectedDate è YYYY-MM-DD in ora locale, perché si confronta con
    // WorkSession.startTime (LocalDateTime lato backend). toISOString() darebbe
    // la data UTC, che la sera tardi in Italia è ancora "ieri".
    //
    // Per gli incarichi di un solo giorno (dataInizio == dataFine o dataFine null)
    // usiamo il giorno dell'incarico invece di oggi: se il collaboratore lavora il
    // giorno prima, il report viene comunque salvato con la data corretta.
    const [selectedDate, setSelectedDate] = useState(() => {
        // La data del report da modificare (deep-link) ha la precedenza sul giorno
        // dell'incarico e su oggi.
        if (initialSelectedDate) return initialSelectedDate;
        if (isIncaricoSingleDay(activeIncarico)) {
            const day = extractDateOnly(activeIncarico?.dataInizio);
            if (day) return day;
        }
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    });
    const [showPendingWarning, setShowPendingWarning] = useState(false);
    const [previewFile, setPreviewFile] = useState<(IncaricoMedia & { isFieldMedia?: boolean }) | null>(null);
    const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
    const [noteText, setNoteText] = useState('');
    const [noteLoading, setNoteLoading] = useState(false);

    // Allinea selectedDate al giorno pianificato per gli incarichi di un giorno,
    // anche quando cambia activeIncarico senza unmount o quando dataInizio
    // arriva dopo il primo render.
    useEffect(() => {
        if (!isIncaricoSingleDay(activeIncarico)) return;
        const day = extractDateOnly(activeIncarico?.dataInizio);
        if (day && day !== selectedDate) setSelectedDate(day);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeIncarico?.id, activeIncarico?.dataInizio, activeIncarico?.dataFine]);

    // Su un incarico REVOCATO il workflow è chiuso dal titolare: non si chiede
    // nessun report e le sessioni pendenti restano solo come storico.
    useEffect(() => {
        const isRevocato = activeIncarico?.stato === 'REVOCATO';
        if (pendingEvidence.hasPending && !isStoricoActive && !isRevocato) setShowPendingWarning(true);
        else setShowPendingWarning(false);
    }, [pendingEvidence.hasPending, isStoricoActive, activeIncarico?.stato]);

    const datiSoggetto = useMemo(() => {
        if (!activeIncarico?.pratica) return null;
        const p = activeIncarico.pratica as any;
        let dati: any;
        try {
            dati = p.datiAggiuntiviJson && typeof p.datiAggiuntiviJson === 'string'
                ? JSON.parse(p.datiAggiuntiviJson)
                : p.datiAggiuntiviJson || p;
        } catch { dati = p; }

        // Formato con soggetti[]
        if (dati.soggetti && Array.isArray(dati.soggetti) && dati.soggetti.length > 0) {
            const soggettoNome = (activeIncarico as any).soggettoNome;
            const s = (soggettoNome
                ? dati.soggetti.find((s: any) => s.nome === soggettoNome)
                : null) ?? dati.soggetti[0];
            // Riporta al formato soggetto_* atteso da InfoTab
            return {
                ...dati,
                soggetto_tipo:              s.tipo,
                soggetto_nome:              s.nome,
                soggetto_nato_a:            s.nato_a,
                soggetto_data_nascita:      s.data_nascita,
                soggetto_codice_fiscale:    s.codice_fiscale,
                soggetto_piva:              s.piva,
                soggetto_sede_via:          s.sede_via,
                soggetto_sede_citta:        s.sede_citta,
                soggetto_sede_cap:          s.sede_cap,
                soggetto_sede_prov:         s.sede_prov,
                soggetto_indirizzo:         s.indirizzo,
                soggetto_citta:             s.citta,
                soggetto_cap:               s.cap,
                soggetto_prov:              s.prov,
                soggetto_telefono:          s.telefono,
                soggetto_email:             s.email,
                soggetto_social:            s.social,
                soggetto_domicilio_altrove: s.domicilio_altrove,
                soggetto_luoghi_frequentati:s.luoghi_frequentati,
                soggetto_altre_info:        s.altre_info,
                soggetto_altre_info_casa:   s.altre_info_casa,
                soggetto_auto:              s.auto,
                soggetto_auto_conoscenti:   s.auto_conoscenti,
                soggetto_moto:              s.moto,
                soggetto_mezzi:             s.mezzi,
                soggetto_lavoro:            s.lavoro,
                soggetto_hobby:             s.hobby,
                soggetto_abitudini:         s.abitudini,
                soggetto_fisico:            s.fisico,
                soggetto_gia_pedinato:      s.gia_pedinato,
                soggetto_attento:           s.attento,
                soggetto_lavoro_particolare:s.lavoro_particolare,
                soggetto_pericoloso:        s.pericoloso,
                soggetto_armi:              s.armi,
                soggetto_allegati_nomi:     s.allegati_nomi,
            };
        }

        return dati;
    }, [activeIncarico]);

    const attivitaGps = useMemo(() => {
        const sessioni = activeIncarico?.sessioni;
        if (!sessioni || !Array.isArray(sessioni)) return { ore: 0, ids: [] };

        // Incarico di un giorno: il collaboratore può lavorare anche il giorno prima,
        // quindi non filtriamo le sessioni sulla finestra di selectedDate (il lavoro
        // resterebbe fuori e il BE rifiuterebbe il report). Prendiamo tutte le sessioni
        // chiuse non ancora reportate con la durata intera.
        // Lo stesso vale per l'incarico continuativo, che ha un solo report (giorno
        // di inizio) e deve includere le sessioni dopo mezzanotte; coerente con
        // ReportTab.isSingleDay e con la ricostruzione sessionIds del BE.
        if (isIncaricoSingleDay(activeIncarico) || (activeIncarico as any).continuativo) {
            const reportedIds = new Set<number>(
                (activeIncarico.relazioni || []).flatMap((r: any) => r.sessionIds || [])
            );
            const filtrate = sessioni.filter((s: any) => {
                if (!s.startTime || !s.endTime) return false;
                return !reportedIds.has(s.id);
            });
            const ms = filtrate.reduce((acc: number, s: any) => {
                const sStart = new Date(s.startTime).getTime();
                const sEnd = new Date(s.endTime).getTime();
                return acc + Math.max(0, sEnd - sStart);
            }, 0);
            return { ore: parseFloat((ms / (1000 * 60 * 60)).toFixed(2)), ids: filtrate.map((s: any) => s.id) };
        }

        // Multi-giorno: una sessione a cavallo della mezzanotte vale per entrambi
        // i giorni, dato che si invia un report per giornata. Quindi:
        // - reportedIds è relativo al giorno selezionato (la stessa sessione può
        // stare in due report)
        // - il filtro usa la sovrapposizione con [00:00, 24:00) invece del giorno di inizio
        // - le ore mostrate sono solo la parte nel giorno selezionato, per non
        // contarle due volte nei totali.
        const dayStart = new Date(`${selectedDate}T00:00:00`).getTime();
        const dayEnd = dayStart + 24 * 60 * 60 * 1000;

        const reportedIdsForDate = new Set<number>(
            (activeIncarico.relazioni || [])
                .filter((r: any) => r.dataRelazione?.substring(0, 10) === selectedDate)
                .flatMap((r: any) => r.sessionIds || [])
        );

        const filtrate = sessioni.filter((s: any) => {
            if (!s.startTime || !s.endTime) return false;
            if (reportedIdsForDate.has(s.id)) return false;
            const sStart = new Date(s.startTime).getTime();
            const sEnd = new Date(s.endTime).getTime();
            return sStart < dayEnd && sEnd > dayStart;
        });

        const ms = filtrate.reduce((acc: number, s: any) => {
            const sStart = new Date(s.startTime).getTime();
            const sEnd = new Date(s.endTime).getTime();
            const overlap = Math.max(0, Math.min(sEnd, dayEnd) - Math.max(sStart, dayStart));
            return acc + overlap;
        }, 0);

        return { ore: parseFloat((ms / (1000 * 60 * 60)).toFixed(2)), ids: filtrate.map((s: any) => s.id) };
    }, [activeIncarico, selectedDate]);

    const mediaGiorno = useMemo(() => {
        if (!activeIncarico) return { tuttiIds: [], noteIds: [] };
        const reportedFileIds = new Set(activeIncarico.relazioni?.flatMap((r: any) => r.fileIdsUfficiali || []) || []);
        const tuttiFiles = (activeIncarico.media || []).filter((m: any) => !reportedFileIds.has(m.id) && !m.filename?.startsWith('Km_') && m.dataAcquisizione);
        const reportedNoteIds = new Set(activeIncarico.relazioni?.flatMap((r: any) => r.noteIdsUfficiali || []) || []);
        const notes = (activeIncarico.noteList || []).filter((note: any) => !reportedNoteIds.has(note.id));
        return { tuttiIds: tuttiFiles.map((m: any) => m.id), noteIds: notes.map((n: any) => n.id) };
    }, [activeIncarico]);

    const reportGiornoCorrente = useMemo(() =>
            activeIncarico?.relazioni?.find((r: any) => r.dataRelazione?.substring(0, 10) === selectedDate) || null,
        [activeIncarico, selectedDate]
    );

    // Nessun contatore sul tab Archivio, come nell'app: i duplicati e i media in
    // approvazione sono nascosti e il numero non tornerebbe con la galleria.

    const handleOpenPreview = async (media: any, fromField = true) => {
        setPreviewFile({ ...media, isFieldMedia: fromField });
        try {
            const freshUrl = await getFreshUrl({ ...media, isFieldMedia: fromField });
            setPreviewFile({ ...media, isFieldMedia: fromField, finalUrl: freshUrl });
        } catch {
            addToast('Errore nel recupero del file originale', 'error');
        }
    };

    const handleSaveNote = async () => {
        if (!activeIncarico || !noteText.trim() || isStoricoActive) return;
        setNoteLoading(true);
        try {
            let lat = 0, lon = 0;
            try { const p = await getPos(); lat = p.coords.latitude; lon = p.coords.longitude; } catch { /* Nessun GPS */ }
            await IncaricoService.addNote(activeIncarico.id, noteText, lat, lon, selectedDate);
            setNoteText('');
            setIsNoteModalOpen(false);
            await onRefresh(true);
            addToast('Nota operativa aggiunta', 'success');
        } catch { addToast('Errore di salvataggio', 'error'); }
        finally { setNoteLoading(false); }
    };

    // Finché l'incarico è ASSEGNATO i tab Report e Archivio sono bloccati:
    // bisogna prima accettarlo. Fascicolo e Operativo (con Accetta/Rifiuta) restano accessibili.
    const isDaAccettare = activeIncarico?.stato === 'ASSEGNATO' && !isStoricoActive;

    const TABS = [
        { id: 'info',     label: 'Fascicolo', icon: <User size={15} /> },
        { id: 'ops',      label: 'Operativo', icon: <Camera size={15} /> },
        { id: 'spese',    label: 'Report',    icon: <FileText size={15} />, lockedByAccept: true },
        { id: 'archivio', label: 'Archivio',  icon: <Folder size={15} />, lockedByAccept: true },
    ] as const;

    const handleTabClick = (tabId: typeof activeTab, lockedByAccept?: boolean) => {
        if (lockedByAccept && isDaAccettare) {
            addToast("Accetta prima l'incarico per accedere a questa sezione", 'info');
            return;
        }
        setActiveTab(tabId);
    };

    return (
        <div className="max-w-3xl mx-auto bg-white min-h-screen shadow-2xl relative flex flex-col">
            {/* Topbar */}
            <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-200 px-4 py-3 flex items-center justify-between">
                <button onClick={onBack} className="p-2 -ml-2 text-slate-500 hover:bg-slate-100 rounded-full transition-colors">
                    <X size={22} />
                </button>
                <div className="text-center flex-1 px-4 truncate">
                    <h2 className="text-sm font-semibold text-slate-900 truncate uppercase leading-tight">
                        {(() => {
                            // Incarico orfano (URGENTE_SENZA_MANDATO senza pratica): solo il nome libero
                            // inserito dal collaboratore, senza prefisso mandato.
                            if ((activeIncarico as any)?.tipoOrigine === 'URGENTE_SENZA_MANDATO' && !activeIncarico?.pratica) {
                                return activeIncarico?.soggettoNome || `Incarico #${activeIncarico?.id}`;
                            }
                            // Su incarico revocato il nome del soggetto non compare nemmeno in topbar:
                            // solo il prefisso della pratica o un segnaposto generico.
                            const praticaName = (activeIncarico?.pratica as any)?.name || '';
                            const prefix = praticaName.includes(' - ') ? praticaName.split(' - ')[0] : praticaName;
                            if (activeIncarico?.stato === 'REVOCATO') {
                                return prefix || `Incarico #${activeIncarico?.id}`;
                            }
                            const soggetto = activeIncarico?.soggettoNome || datiSoggetto?.soggetto_nome || datiSoggetto?.soggetto_denominazione || '';
                            return soggetto ? `${prefix} - ${soggetto}` : prefix || `Incarico #${activeIncarico?.id}`;
                        })()}
                    </h2>
                </div>
                <button onClick={() => onRefresh(true)} className="p-2 -mr-2 text-slate-400 hover:text-slate-600 rounded-full transition-colors">
                    {pollingInterval.current ? <Loader2 size={20} className="animate-spin text-blue-500" /> : <RefreshCw size={20} />}
                </button>
            </div>

            {/*
              Report dimenticato: incarico passato senza relazione inviata. Il workflow
              resta modificabile, quindi mostriamo un invito a compilare invece del banner
              "Sola lettura".
            */}
            {isStoricoActive && (
                (!activeIncarico?.relazioni || activeIncarico.relazioni.length === 0)
                    ? (
                        <div className="bg-amber-50 border-b border-amber-200 text-amber-800 px-4 py-3 text-xs font-bold uppercase tracking-wider flex items-center gap-2 justify-center">
                            <FileText size={16} /> Incarico concluso — compila il report
                        </div>
                    )
                    : (
                        <div className="bg-amber-50 border-b border-amber-200 text-amber-800 px-4 py-3 text-xs font-bold uppercase tracking-wider flex items-center gap-2 justify-center">
                            <Archive size={16} /> Archivio Storico Sola Lettura
                        </div>
                    )
            )}

            {/* Tab selector */}
            <div className="px-4 py-3 bg-white border-b border-slate-100 sticky top-[57px] z-30">
                <div className="flex p-1 bg-slate-100 rounded-xl relative">
                    {pendingEvidence.hasPending && (
                        <span className="absolute top-0 right-0 -mt-1 -mr-1 flex h-3 w-3 z-50">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
                        </span>
                    )}
                    {TABS.map(t => {
                        const isLocked = (t as any).lockedByAccept && isDaAccettare;
                        return (
                            <button
                                key={t.id}
                                onClick={() => handleTabClick(t.id, (t as any).lockedByAccept)}
                                aria-disabled={isLocked}
                                title={isLocked ? "Accetta prima l'incarico" : undefined}
                                className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-[11px] font-semibold rounded-lg transition-all relative ${activeTab === t.id ? 'bg-white text-slate-900 shadow-sm' : isLocked ? 'text-slate-300 cursor-not-allowed' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                {t.icon}
                                <span className="hidden sm:inline-block">{t.label}</span>
                                {isLocked && (
                                    <Lock size={10} className="text-slate-400" />
                                )}
                                {(t as any).badge != null && (
                                    <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-[8px] font-black min-w-[16px] h-4 flex items-center justify-center rounded-full px-1 shadow">
                                        {(t as any).badge}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Contenuto tab */}
            <div className="p-5 sm:p-8 flex-1 pb-32">
                {activeTab === 'info' && (
                    <InfoTab activeIncarico={activeIncarico} datiSoggetto={datiSoggetto} onOpenPreview={handleOpenPreview} />
                )}
                {activeTab === 'ops' && (
                    <OpsTab
                        activeIncarico={activeIncarico}
                        isStoricoActive={isStoricoActive}
                        isFuturoActive={isFuturoActive}
                        attivitaGps={attivitaGps}
                        actionLoading={actionLoading}
                        fotoDevice={fotoDevice}
                        setFotoDevice={setFotoDevice}
                        videoDevice={videoDevice}
                        setVideoDevice={setVideoDevice}
                        onOpenNote={() => setIsNoteModalOpen(true)}
                        onStartWithPhoto={onStartWithPhoto}
                        onStopWithPhoto={onStopWithPhoto}
                        onRefresh={onRefresh}
                        onAccetta={onAccetta}
                        onRifiuta={onRifiuta}
                        onCreateUrgente={onCreateUrgente}
                    />
                )}
                {activeTab === 'spese' && (
                    <ReportTab
                        activeIncarico={activeIncarico}
                        isStoricoActive={isStoricoActive}
                        selectedDate={selectedDate}
                        setSelectedDate={setSelectedDate}
                        attivitaGps={attivitaGps}
                        mediaGiorno={mediaGiorno}
                        reportGiornoCorrente={reportGiornoCorrente}
                        onReportSent={() => onRefresh(true)}
                    />
                )}
                {activeTab === 'archivio' && (
                    <ArchivioTab
                        media={activeIncarico?.media || []}
                        getFreshUrl={getFreshUrl}
                        onRequestDelete={async (mediaId, filename) => {
                            try {
                                // DELETE /media/{id}: per il collaboratore il backend crea una DELETION_REQUEST
                                // e risponde data=false con un messaggio; admin e responsabile eliminano subito.
                                const res: any = await IncaricoService.deleteMedia(mediaId);
                                const msg = res?.message
                                    || (res?.data === true ? `"${filename}" eliminato`
                                        : 'Richiesta di eliminazione inviata al titolare');
                                addToast(msg, 'success');
                                onRefresh(true);
                            } catch (e: any) {
                                console.error('[ArchivioTab.onRequestDelete]', e);
                                const msg = e?.response?.data?.message || 'Errore richiesta eliminazione';
                                addToast(msg, 'error');
                                throw e;
                            }
                        }}
                        onRequestBatchDelete={async (mediaIds) => {
                            // Stessa DELETE per ogni id; per il collaboratore diventano tutte richieste da
                            // approvare. Raccogliamo gli errori e mostriamo un solo toast.
                            const results = await Promise.allSettled(
                                mediaIds.map(id => IncaricoService.deleteMedia(id))
                            );
                            const ok = results.filter(r => r.status === 'fulfilled').length;
                            const ko = results.length - ok;
                            if (ko === 0) {
                                addToast(`Richiesta eliminazione inviata per ${ok} file`, 'success');
                            } else if (ok === 0) {
                                addToast(`Nessuna richiesta inviata (${ko} errori)`, 'error');
                            } else {
                                addToast(`${ok} richieste inviate, ${ko} errori`, 'info');
                            }
                            onRefresh(true);
                        }}
                    />
                )}
            </div>

            {/* Modali */}
            {showPendingWarning && pendingEvidence.hasPending && !isStoricoActive && activeIncarico?.stato !== 'REVOCATO' && (
                <PendingWarningModal
                    sessions={pendingEvidence.sessions}
                    onLater={() => setShowPendingWarning(false)}
                    onGoToReport={() => { setShowPendingWarning(false); setActiveTab('spese'); }}
                />
            )}
            {previewFile && <PreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />}
            {isNoteModalOpen && (
                <NoteModal
                    noteText={noteText}
                    setNoteText={setNoteText}
                    actionLoading={noteLoading}
                    onSave={handleSaveNote}
                    onClose={() => setIsNoteModalOpen(false)}
                />
            )}
            {missingDateQueue.length > 0 && (
                <MissingDateModal
                    queue={missingDateQueue}
                    currentIndex={currentMissingDateIndex}
                    manualFileDate={manualFileDate}
                    setManualFileDate={setManualFileDate}
                    onConfirm={onManualDateSubmit}
                    onSkip={onSkipMissingDate}
                />
            )}
        </div>
    );
};

export default IncaricoDetailView;